import "../sellscreendialogs.scss";
import {
    type FC,
    type RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { useDebounce } from "@onshopfront/components";
import moment from "moment";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../../_Bootstrap/hooks";
import { hideDialog } from "../../../../Actions/Dialogs/DialogActions";
import { type GiftCardPayment } from "../../../../Actions/SellScreen/SellScreenPaymentActions";
import {
    addGiftCardToSale,
    type SaleAddGiftCardOptions,
} from "../../../../Actions/SellScreen/SellScreenProductActions";
import Button from "../../../../Common/Buttons/Button";
import StatusText from "../../../../Common/Forms/StatusText";
import PopupManager from "../../../../Common/Popups/PopupManager";
import { getEnterpriseAuthToken } from "../../../../Data/_Bases/BaseEnterpriseRepository";
import { determineDataSource } from "../../../../Data/DataStores/DataSource";
import { ApplicationEventsFromShopfront } from "../../../../Utilities/Platform/Applications/ApplicationEvents";
import { getApplicationManager } from "../../../../Utilities/Platform/Applications/ApplicationUtilities";
import { getOption } from "../../../../Utilities/Platform/Options/Options";
import { getPolicy } from "../../../../Utilities/Platform/Policies/Policies";
import { getMainHost, getVendorSubdomain } from "../../../../Utilities/Platform/Request";

export const getExistingGiftCards = async (
    giftCardSource: string,
    code: string,
    outlet: string | null,
    connection: string,
    allowPending = false
): Promise<Array<GiftCardPayment>> => {
    if(giftCardSource === "internal") {
        // Load from the local file
        const source = await determineDataSource();
        const giftCards = await source.giftCards.code(code);
        const shareGiftCards = getOption("share_gift_cards");

        let giftCardExists = false;
        let hasPending = false;
        let hasActive = false;
        const validGiftCards = [];
        const activeGiftCards = [];

        for(let i = 0, l = giftCards.length; i < l; i++) {
            // Check to see if the gift card is outside the outlet
            if(!shareGiftCards && outlet) {
                if(giftCards[i].outlet_id !== outlet) {
                    continue;
                }
            }

            if(giftCards[i].status === "cancelled") {
                continue;
            }

            const current = parseFloat(giftCards[i].current_amount);

            // Check to see if there is a gift card with remaining amount on them
            if(current === 0 || isNaN(current)) {
                continue;
            }

            // Check to see if the gift card is expired
            if(moment(giftCards[i].expiry).isBefore(moment())) {
                continue;
            }

            if(giftCards[i].status === "pending") {
                if(!allowPending) {
                    continue;
                }

                hasPending = true;
            } else if(giftCards[i].status === "active") {
                hasActive = true;
                activeGiftCards.push(giftCards[i]);
            }

            // There is a valid gift card
            giftCardExists = true;
            validGiftCards.push(giftCards[i]);
        }

        let giftCardsToUse = validGiftCards;

        if(hasActive && hasPending) {
            giftCardsToUse = activeGiftCards;
        }

        if(!hasActive && giftCardExists && hasPending) {
            PopupManager.warning(`The gift card code (${code}) is used on a parked sale`);
        }

        return giftCardsToUse.map(giftCard => ({
            ...giftCard,
            id             : giftCard.uuid,
            current_amount : parseFloat(giftCard.current_amount),
            original_amount: parseFloat(giftCard.original_amount),
            source         : "internal",
        })).toSorted((a, b) => {
            if(a.status === b.status) {
                return 0;
            }

            if(a.status === "active") {
                return 1;
            }

            if(b.status === "active") {
                return -1;
            }

            if(a.status === "pending") {
                return 1;
            }

            if(b.status === "pending") {
                return -1;
            }

            return 0;
        });
    }

    // Load from a network request
    // Use raw fetch instead of our request object
    const response = await fetch(`${giftCardSource}/${code}`, {
        headers: {
            "x-shopfront-token" : await getEnterpriseAuthToken(connection),
            "x-shopfront-vendor": getVendorSubdomain(),
            "x-shopfront-host"  : getMainHost(),
        },
    });

    if(response.status === 404) {
        return [];
    }

    if(response.status !== 200) {
        throw new Error(`Invalid status returned from gift card source: ${response.status}`);
    }

    const giftCard = await response.json();

    if(giftCard.status === "reserved") {
        return [];
    }

    return [{
        ...giftCard,
        id    : giftCard.code,
        source: connection,
    }];
};

export const RequestCode: FC<SaleAddGiftCardOptions> = (options) => {
    const [ value, setValue ] = useState("");
    const dispatch = useShopfrontDispatch();

    const outlet = useShopfrontSelector(state => state.location.outlet ? state.location.outlet.id : null);
    const saleCodes = useShopfrontSelector(state => {
        return state.sellscreen.sale.products
            .filter(product => product.giftCard)
            .map(product => product.giftCard?.code.toLowerCase());
    });

    const [ source, setSource ] = useState("internal");

    const inputRef = useRef<StatusText | null>(null);

    const submitRef = useRef({
        canSubmit           : false,
        submitWhenChecked   : false,
        checking            : false,
        receivedError       : false,
        ignoreError         : false,
        checkingIntegrations: false,
        checkingExisting    : false,
    });

    useEffect(() => {
        if(options.source === "auto" || options.source === "internal") {
            setSource("internal");
        } else {
            const sources = getPolicy("gift_card_sources", []);

            for(let i = 0, l = sources.length; i < l; i++) {
                if(sources[i].connection === options.source) {
                    setSource(sources[i].address);

                    return;
                }
            }

            throw new Error("Unable to find gift card source address");
        }
    }, [ options.source ]);

    const addCode = useCallback(() => {
        if(!value) {
            PopupManager.error("Please add a valid code for the gift card");

            return;
        }

        if(value.length > 60) {
            PopupManager.error("The Gift Card code should be 60 characters or less.");

            return;
        }

        if(submitRef.current.submitWhenChecked) {
            return;
        }

        if(submitRef.current.receivedError && !submitRef.current.ignoreError) {
            PopupManager.warning(
                "We received an error while checking if the gift card is valid, if you'd like to continue anyway, " +
                "please press the add button again"
            );
            submitRef.current.ignoreError = true;
        } else if(submitRef.current.canSubmit) {
            dispatch(hideDialog());
            dispatch(addGiftCardToSale({
                ...options,
                code: value,
            }));
        } else if(submitRef.current.checking) {
            submitRef.current.submitWhenChecked = true;
        } else {
            PopupManager.error(`The gift card code "${value}" is already in use`);
        }
    }, [ dispatch, options, value ]);

    const renderInput = useCallback(
        (inputRef: RefObject<HTMLInputElement | null>, onChange: (value: string) => void) => {
            return (
                <input
                    ref={inputRef}
                    autoFocus
                    onChange={e => onChange(e.currentTarget.value)}
                    onKeyDown={e => {
                        if(e.key === "Enter") {
                            addCode();
                        }
                    }}
                    value={value}
                />
            );
        },
        [ addCode, value ]
    );

    const statusCallback = (code: string, statusCallback: (result: boolean) => void) => {
        return new Promise((res) => {
            submitRef.current.canSubmit = false;

            const found = saleCodes.includes(code.toLowerCase());

            if(found || !code || code.length > 60) {
                res(false);
                statusCallback(false);

                return;
            }

            submitRef.current.receivedError = false;
            submitRef.current.ignoreError = false;

            return getExistingGiftCards(source, code, outlet, options.source)
                .then(async codes => {
                    let exists = codes.length > 0;

                    if(getApplicationManager().hasApplications()) {
                        const pipelineResponse = await getApplicationManager().sendPipeline(
                            ApplicationEventsFromShopfront.GIFT_CARD_CODE_CHECK,
                            {
                                code   : value,
                                message: null,
                            },
                            {}
                        );

                        if(pipelineResponse.message) {
                            PopupManager.error(pipelineResponse.message);
                            exists = true;
                        }
                    }

                    submitRef.current.canSubmit = !exists;

                    return !exists;
                })
                .catch(err => {
                    console.error("Getting gift cards by code failed", err);
                    submitRef.current.receivedError = true;

                    return false;
                })
                .then(result => {
                    statusCallback(result);

                    if(submitRef.current.submitWhenChecked) {
                        submitRef.current.submitWhenChecked = false;

                        if(result) {
                            addCode();
                        } else {
                            inputRef.current?.input.current?.select();
                            PopupManager.error(`The Gift Card code "${code}" is already in use`);
                        }
                    }

                    return result;
                })
                .finally(() => {
                    submitRef.current.checking = false;
                    res(true);
                });
        });
    };

    const [ checkStatusCallback ] = useDebounce(statusCallback, 400);

    return (
        <div className="request-code">
            <p>Please enter or scan the gift card&apos;s code</p>
            <StatusText
                value={value}
                errorText="Code is already taken"
                onChange={setValue}
                checkStatus={checkStatusCallback}
                renderInput={renderInput}
                checkStart={(): void => {
                    submitRef.current.checking = true;
                }}
                ref={inputRef}
            />
            <div className="buttons">
                <Button
                    type="button"
                    classes={[ "button-cancel" ]}
                    onClick={() => dispatch(hideDialog())}
                >
                    Cancel
                </Button>
                <Button
                    type="button"
                    classes={[ "button-save" ]}
                    onClick={() => addCode()}
                >
                    Add
                </Button>
            </div>
        </div>
    );
};
